import { useState, useEffect, useCallback, useRef } from 'react';
import './ReceiverDetail.css';
import timezones from '../utils/timezones';
import LocationPicker from './LocationPicker';
import SetupInstructions from './SetupInstructions';
import ViewerManagement from './ViewerManagement';
import ReceiverConfigForm from './ReceiverConfigForm';
import PerfCharts from './PerfCharts';
import { hasEverSentStats } from '../utils/receiverSoftware';
import { getReceiverDebugBuffer } from '../utils/httpApi';
import { isApiSuccess } from '../utils/api';
import { formatRelativeTime } from '../utils/dateTime';

function ReceiverDetail({
  receiver,
  receiverStatistics = null,
  receiverMeta = undefined,       // getReceiversMetadata entry; undefined = not loaded yet
  latestReceiverVersion = null,
  onNavigate = null,
  onSave,
  onDelete,
  token,
}) {
  // Only assert "not Aviotrack" once the metadata has been fetched at all —
  // before that, absence means "unknown", not "readsb".
  const isAviotrack = receiverMeta === undefined ? undefined : hasEverSentStats(receiverMeta);
  const [formData, setFormData] = useState({
    receiverUuid: receiver.receiverUuid || '',
    receiverName: receiver.receiverName || '',
    latitude: receiver.latitude?.toString() || '',
    longitude: receiver.longitude?.toString() || '',
    antennaType: receiver.antennaType || '',
    receiverType: receiver.receiverType || '',
    softwareVersion: receiver.softwareVersion || '',
    timezone: receiver.timezone || 'UTC',
    airportIcao: receiver.airportIcao || '',
    notes: receiver.notes || '',
    distanceUnits: receiver.distanceUnits || 'metric',
    rangeRingsNumber: receiver.rangeRingsNumber?.toString() || '5',
    rangeRingsInterval: receiver.rangeRingsInterval?.toString() || '50',
    maxDistance: receiver.maxDistance?.toString() || '250',
    antennaHeightAboveGround: receiver.antennaHeightAboveGround?.toString() || '',
    upInTheAirCode: receiver.upInTheAirCode || '',
    isActive: receiver.isActive ?? true,
    isMobile: receiver.isMobile ?? false,
    // Detection configuration
    airborneDetectionEnabled: receiver.airborneDetectionEnabled ?? true,
    simulationEnabled: receiver.simulationEnabled ?? true,
    detectionAirportList: receiver.detectionAirportList || '',
    maxReceiverAirportRangeKm: receiver.maxReceiverAirportRangeKm?.toString() || '40',
    maxAircraftAirportRangeKm: receiver.maxAircraftAirportRangeKm?.toString() || '15',
    maxDetectionAltitudeFt: receiver.maxDetectionAltitudeFt?.toString() || '10000',
    requireRunwayAlignment: receiver.requireRunwayAlignment ?? true,
    runwayHeadingToleranceDeg: receiver.runwayHeadingToleranceDeg?.toString() || '30'
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [apiErrors, setApiErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const viewerManagementRef = useRef(null);
  const [hasPendingViewer, setHasPendingViewer] = useState(false);
  const [uuidCopied, setUuidCopied] = useState(false);
  // Sub-tab inside the receiver detail view: 'settings' (existing form),
  // 'config' (remote config push), 'stats' (graphs1090-style perf charts).
  // Honour {_initialTab} on the receiver prop so the Receivers/All-Receivers
  // list deep-links land on the right tab. Default is 'config' (the Device
  // tab) — that's where the operator usually wants to look first; the
  // Receiver tab is for one-time setup edits.
  //
  // Tab IDs are stable contract: 'config' = Device, 'settings' = Receiver,
  // 'stats' = Performance. Don't rename them — deep links in Admin.js,
  // App.js and Receivers.js reference them by ID.
  const [activeTab, setActiveTab] = useState(receiver._initialTab || 'settings');

  // Update formData when receiver changes (but not for lastActiveAt or isActive which change frequently)
  useEffect(() => {
    setFormData({
      receiverUuid: receiver.receiverUuid || '',
      receiverName: receiver.receiverName || '',
      latitude: receiver.latitude?.toString() || '',
      longitude: receiver.longitude?.toString() || '',
      antennaType: receiver.antennaType || '',
      receiverType: receiver.receiverType || '',
      softwareVersion: receiver.softwareVersion || '',
      timezone: receiver.timezone || 'UTC',
      airportIcao: receiver.airportIcao || '',
      notes: receiver.notes || '',
      distanceUnits: receiver.distanceUnits || 'metric',
      rangeRingsNumber: receiver.rangeRingsNumber?.toString() || '5',
      rangeRingsInterval: receiver.rangeRingsInterval?.toString() || '50',
      maxDistance: receiver.maxDistance?.toString() || '250',
      antennaHeightAboveGround: receiver.antennaHeightAboveGround?.toString() || '',
      upInTheAirCode: receiver.upInTheAirCode || '',
      isActive: receiver.isActive ?? true,
      isMobile: receiver.isMobile ?? false,
      // Detection configuration
      airborneDetectionEnabled: receiver.airborneDetectionEnabled ?? true,
      simulationEnabled: receiver.simulationEnabled ?? true,
      detectionAirportList: receiver.detectionAirportList || '',
      maxReceiverAirportRangeKm: receiver.maxReceiverAirportRangeKm?.toString() || '40',
      maxAircraftAirportRangeKm: receiver.maxAircraftAirportRangeKm?.toString() || '15',
      maxDetectionAltitudeFt: receiver.maxDetectionAltitudeFt?.toString() || '10000',
      requireRunwayAlignment: receiver.requireRunwayAlignment ?? true,
      runwayHeadingToleranceDeg: receiver.runwayHeadingToleranceDeg?.toString() || '30'
    });
    setHasChanges(false);
  }, [
    receiver.receiverUuid,
    receiver.receiverName,
    receiver.latitude,
    receiver.longitude,
    receiver.antennaType,
    receiver.receiverType,
    receiver.softwareVersion,
    receiver.timezone,
    receiver.airportIcao,
    receiver.notes,
    receiver.distanceUnits,
    receiver.rangeRingsNumber,
    receiver.rangeRingsInterval,
    receiver.maxDistance,
    receiver.antennaHeightAboveGround,
    receiver.upInTheAirCode,
    receiver.isMobile,
    receiver.airborneDetectionEnabled,
    receiver.simulationEnabled,
    receiver.detectionAirportList,
    receiver.maxReceiverAirportRangeKm,
    receiver.maxAircraftAirportRangeKm,
    receiver.maxDetectionAltitudeFt,
    receiver.requireRunwayAlignment,
    receiver.runwayHeadingToleranceDeg,
    receiver.isActive
  ]);

  // Debug buffer state
  const [debugBufferOpen, setDebugBufferOpen] = useState(false);
  const [debugEntries, setDebugEntries] = useState([]);
  const [debugLoading, setDebugLoading] = useState(false);

  const fetchDebugBuffer = useCallback(async () => {
    if (!token || !receiver.receiverUuid) return;
    setDebugLoading(true);
    try {
      const result = await getReceiverDebugBuffer(receiver.receiverUuid, token);
      if (isApiSuccess(result)) {
        setDebugEntries(result.entries || []);
      }
    } catch (e) {
      console.error('Failed to fetch debug buffer:', e);
    } finally {
      setDebugLoading(false);
    }
  }, [token, receiver.receiverUuid]);

  const handleDebugToggle = useCallback(() => {
    const opening = !debugBufferOpen;
    setDebugBufferOpen(opening);
    if (opening) fetchDebugBuffer();
  }, [debugBufferOpen, fetchDebugBuffer]);

  // Check if there are changes
  useEffect(() => {
    const changed =
      formData.receiverUuid !== (receiver.receiverUuid || '') ||
      formData.receiverName !== (receiver.receiverName || '') ||
      formData.latitude !== (receiver.latitude?.toString() || '') ||
      formData.longitude !== (receiver.longitude?.toString() || '') ||
      formData.antennaType !== (receiver.antennaType || '') ||
      formData.receiverType !== (receiver.receiverType || '') ||
      formData.softwareVersion !== (receiver.softwareVersion || '') ||
      formData.timezone !== (receiver.timezone || 'UTC') ||
      formData.airportIcao !== (receiver.airportIcao || '') ||
      formData.notes !== (receiver.notes || '') ||
      formData.distanceUnits !== (receiver.distanceUnits || 'metric') ||
      formData.rangeRingsNumber !== (receiver.rangeRingsNumber?.toString() || '5') ||
      formData.rangeRingsInterval !== (receiver.rangeRingsInterval?.toString() || '50') ||
      formData.maxDistance !== (receiver.maxDistance?.toString() || '250') ||
      formData.antennaHeightAboveGround !== (receiver.antennaHeightAboveGround?.toString() || '') ||
      formData.upInTheAirCode !== (receiver.upInTheAirCode || '') ||
      formData.isActive !== (receiver.isActive ?? true) ||
      formData.isMobile !== (receiver.isMobile ?? false) ||
      // Detection configuration
      formData.airborneDetectionEnabled !== (receiver.airborneDetectionEnabled ?? true) ||
      formData.simulationEnabled !== (receiver.simulationEnabled ?? true) ||
      formData.detectionAirportList !== (receiver.detectionAirportList || '') ||
      formData.maxReceiverAirportRangeKm !== (receiver.maxReceiverAirportRangeKm?.toString() || '40') ||
      formData.maxAircraftAirportRangeKm !== (receiver.maxAircraftAirportRangeKm?.toString() || '15') ||
      formData.maxDetectionAltitudeFt !== (receiver.maxDetectionAltitudeFt?.toString() || '10000') ||
      formData.requireRunwayAlignment !== (receiver.requireRunwayAlignment ?? true) ||
      formData.runwayHeadingToleranceDeg !== (receiver.runwayHeadingToleranceDeg?.toString() || '30');

    setHasChanges(changed);
  }, [formData, receiver]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLocationChange = (lat, lng) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString()
    }));
  };

  const handleCopyInstruction = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const handleCopyUuid = () => {
    navigator.clipboard.writeText(formData.receiverUuid).then(() => {
      setUuidCopied(true);
      setTimeout(() => setUuidCopied(false), 2000);
    }).catch(() => {});
  };

  const handleSave = async () => {
    // Clear previous messages
    setApiErrors([]);
    setSuccessMessage('');

    // Send all editable fields (isActive is backend-managed, not editable)
    const updateData = {
      receiverUuid: receiver.receiverUuid,
      newReceiverUuid: formData.receiverUuid !== receiver.receiverUuid ? formData.receiverUuid : null,
      receiverName: formData.receiverName,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      antennaType: formData.antennaType || null,
      receiverType: formData.receiverType || null,
      softwareVersion: formData.softwareVersion || null,
      timezone: formData.timezone,
      airportIcao: formData.airportIcao || null,
      notes: formData.notes || null,
      distanceUnits: formData.distanceUnits,
      rangeRingsNumber: formData.rangeRingsNumber ? parseInt(formData.rangeRingsNumber) : null,
      rangeRingsInterval: formData.rangeRingsInterval ? parseInt(formData.rangeRingsInterval) : null,
      maxDistance: formData.maxDistance ? parseInt(formData.maxDistance) : null,
      antennaHeightAboveGround: formData.antennaHeightAboveGround ? parseFloat(formData.antennaHeightAboveGround) : null,
      upInTheAirCode: formData.upInTheAirCode || null,
      isMobile: formData.isMobile,
      // Detection configuration
      airborneDetectionEnabled: formData.airborneDetectionEnabled,
      simulationEnabled: formData.simulationEnabled,
      detectionAirportList: formData.detectionAirportList || null,
      maxReceiverAirportRangeKm: formData.maxReceiverAirportRangeKm ? parseFloat(formData.maxReceiverAirportRangeKm) : null,
      maxAircraftAirportRangeKm: formData.maxAircraftAirportRangeKm ? parseFloat(formData.maxAircraftAirportRangeKm) : null,
      maxDetectionAltitudeFt: formData.maxDetectionAltitudeFt ? parseInt(formData.maxDetectionAltitudeFt) : null,
      requireRunwayAlignment: formData.requireRunwayAlignment,
      runwayHeadingToleranceDeg: formData.runwayHeadingToleranceDeg ? parseFloat(formData.runwayHeadingToleranceDeg) : null
    };

    const messages = [];
    if (hasChanges) {
      const result = await onSave(updateData);
      if (!result.success) {
        setApiErrors(result.errors || ['An unknown error occurred']);
        return;
      }
      setHasChanges(false);
      messages.push(result.message || 'Receiver updated successfully');
    }

    // A viewer email typed but not added is part of what Save means. Report its
    // outcome too: a success banner while the viewer was never added is how a
    // share was silently lost.
    if (hasPendingViewer && viewerManagementRef.current) {
      const viewerError = await viewerManagementRef.current.addPendingViewer();
      if (viewerError) {
        setApiErrors([`Viewer not added: ${viewerError}`]);
        return;
      }
      messages.push('Viewer added');
    }

    if (messages.length > 0) {
      setSuccessMessage(messages.join(' · '));
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    // Clear previous messages
    setApiErrors([]);
    setSuccessMessage('');

    if (onDelete) {
      const result = await onDelete(receiver.receiverUuid);

      if (result.success) {
        // Success - the receiver will be removed from state by parent
        setShowDeleteConfirm(false);
      } else {
        // Error - display errors and keep modal open
        setApiErrors(result.errors || ['An unknown error occurred']);
      }
    } else {
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString();
  };

  // Use the centralized helper that knows how to parse the bare
  // LocalDateTime strings the backend emits (no Z suffix). Without this
  // step JS treats the string as local time, producing the recurring
  // "1 hour ago" off-by-tz-offset bug for users in BST/WEST.
  const formatDate = (dateString) => formatRelativeTime(dateString);

  // Status comes from the backend's isActive flag only
  // The backend tracks active WebSocket connections from all receivers
  const status = receiver.isActive ? 'online' : 'offline';

  return (
    <div className="receiver-detail">
      <div className="detail-header">
        <div className={`receiver-status-large ${status}`}>
          {status.toUpperCase()}
        </div>
      </div>

      {/* Display success message */}
      {successMessage && (
        <div className="api-success-banner">
          <h4>✓ {successMessage}</h4>
        </div>
      )}

      {/* Display API errors */}
      {apiErrors.length > 0 && (
        <div className="api-error-banner">
          <h4>⚠ Error</h4>
          <ul>
            {apiErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="detail-stats">
        <div className="stat-card">
          <div className="stat-label">Last Active</div>
          <div className="stat-value-large stat-value-text">{formatDate(receiver.lastActiveAt)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total All Time</div>
          <div className="stat-value-large">{formatNumber(receiverStatistics?.totalUniqueAircraftAllTime || 0)}</div>
        </div>
        <div className="stat-card highlight-card">
          <div className="stat-label">Total Today</div>
          <div className="stat-value-large">{formatNumber(receiverStatistics?.totalUniqueAircraftToday || 0)}</div>
        </div>
        <div className="stat-card highlight-card-success">
          <div className="stat-label">New Today</div>
          <div className="stat-value-large">{formatNumber(receiverStatistics?.newAircraftToday || 0)}</div>
        </div>
        <div className="stat-card highlight-card">
          <div className="stat-label">Messages Today</div>
          <div className="stat-value-large">{formatNumber(receiverStatistics?.totalMessagesToday || 0)}</div>
        </div>
      </div>

      {/* Sub-tabs (display order = Receiver → Device → Performance):
          - Receiver    (ID 'settings') — the backend's view of the receiver: name,
                                          location, sharing, viewers, identity. First
                                          and default: it applies to every receiver.
          - Device      (ID 'config')   — config that lives on the receiver hardware;
                                          edits are CFG-PUSH'd to the device.
          - Performance (ID 'stats')    — live charts + Connection Debug Log.
          Tab IDs are kept stable for deep-link compatibility. */}
      <div className="airport-content-tabs receiver-detail-tabs">
        <button
          type="button"
          className={`content-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >Receiver</button>
        <button
          type="button"
          className={`content-tab ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveTab('config')}
        >Device</button>
        <button
          type="button"
          className={`content-tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >Performance</button>
      </div>

      {activeTab === 'config' && (
        <ReceiverConfigForm
          receiverUuid={receiver.receiverUuid}
          token={token}
          isAviotrack={isAviotrack}
          declaredReceiverType={receiver.receiverType}
          latestReceiverVersion={latestReceiverVersion}
          onNavigate={onNavigate}
        />
      )}

      {activeTab === 'stats' && (
        <>
          <PerfCharts
            receiverUuid={receiver.receiverUuid}
            token={token}
            isAviotrack={isAviotrack}
            latestReceiverVersion={latestReceiverVersion}
            onNavigate={onNavigate}
          />

          {/* Connection Debug Log — moved here from the Settings tab.
              It belongs alongside the performance charts because both are
              about diagnosing the receiver's live state, not editing the
              persistent receiver record. */}
          <div className="detail-section">
            <div className="debug-buffer-header" onClick={handleDebugToggle} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h3 style={{ margin: 0 }}>Connection Debug Log</h3>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary, #888)' }}>
                {debugBufferOpen ? '▼' : '▶'} {debugBufferOpen ? 'Click to hide' : 'Click to show last 10 messages'}
              </span>
              {debugBufferOpen && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); fetchDebugBuffer(); }}
                  className="btn-secondary"
                  style={{ marginLeft: 'auto', padding: '2px 10px', fontSize: '12px' }}
                  disabled={debugLoading}
                >
                  {debugLoading ? 'Loading...' : 'Refresh'}
                </button>
              )}
            </div>
            {debugBufferOpen && (
              <div className="debug-buffer-content" style={{ marginTop: '10px' }}>
                {debugLoading && debugEntries.length === 0 ? (
                  <p style={{ color: 'var(--text-secondary, #888)', fontStyle: 'italic' }}>Loading...</p>
                ) : debugEntries.length === 0 ? (
                  <p style={{ color: 'var(--text-secondary, #888)', fontStyle: 'italic' }}>No debug entries available. Entries are recorded when the receiver connects.</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="debug-buffer-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: 'monospace' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-color, #333)', textAlign: 'left' }}>
                          <th style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>Time</th>
                          <th style={{ padding: '4px 8px' }}>Type</th>
                          <th style={{ padding: '4px 8px' }}>State</th>
                          <th style={{ padding: '4px 8px' }}>Connection</th>
                          <th style={{ padding: '4px 8px' }}>Source IP</th>
                          <th style={{ padding: '4px 8px' }}>Detail / Raw Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {debugEntries.map((entry, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid var(--border-color, #222)' }}>
                            <td style={{ padding: '4px 8px', whiteSpace: 'nowrap', color: 'var(--text-secondary, #aaa)' }}>
                              {entry.timestamp ? new Date(entry.timestamp + 'Z').toLocaleTimeString() : '-'}
                            </td>
                            <td style={{ padding: '4px 8px' }}>
                              <span className={`debug-type-badge debug-type-${(entry.messageType || '').toLowerCase()}`}>
                                {entry.messageType || '-'}
                              </span>
                            </td>
                            <td style={{ padding: '4px 8px', color: entry.connectionState === 'IDENTIFIED' ? 'var(--success-color, #4caf50)' : 'var(--warning-color, #ff9800)' }}>
                              {entry.connectionState || '-'}
                            </td>
                            <td style={{ padding: '4px 8px', color: 'var(--text-secondary, #aaa)' }}>
                              {entry.connectionId || '-'}
                            </td>
                            <td style={{ padding: '4px 8px', color: 'var(--text-secondary, #aaa)' }}>
                              {entry.sourceIp || '-'}
                            </td>
                            <td style={{ padding: '4px 8px', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {entry.detail || entry.rawHexTruncated || '-'}
                              {entry.fullLengthBytes > 0 && !entry.detail && (
                                <span style={{ color: 'var(--text-secondary, #666)', marginLeft: '4px' }}>
                                  ({entry.fullLengthBytes}B)
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'settings' && (
      <>
      <div className="detail-sections">
        <div className="detail-section">
          <h3>Basic Information</h3>
          <div className="detail-grid">
            <div className="detail-field">
              <label>Receiver UUID</label>
              <div className="uuid-field-container">
                <input
                  type="text"
                  name="receiverUuid"
                  value={formData.receiverUuid}
                  onChange={handleChange}
                  placeholder="e.g. bee00003-8172-d18c-bee0-00038172d18c"
                  className="field-input uuid-input"
                />
                <button
                  type="button"
                  className="btn-copy-uuid"
                  onClick={handleCopyUuid}
                  title="Copy UUID"
                >
                  {uuidCopied ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 2a2 2 0 0 1 2-2h4.586a1 1 0 0 1 .707.293l2.414 2.414a1 1 0 0 1 .293.707V11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2z" stroke="currentColor" strokeWidth="1" fill="none"/>
                      <path d="M2 6h2v7a1 1 0 0 0 1 1h5v2H5a2 2 0 0 1-2-2V6z" fill="currentColor"/>
                    </svg>
                  )}
                </button>
              </div>
              <span className="field-note">Paste a hardware UUID (e.g. ADSBee) to link this receiver to a specific device</span>
            </div>
            <div className="detail-field">
              <label>Receiver Name</label>
              <input
                type="text"
                name="receiverName"
                value={formData.receiverName}
                onChange={handleChange}
                className="field-input"
              />
            </div>
            <div className="detail-field">
              <label>IP Address</label>
              <input
                type="text"
                value={receiver.ipAddress || 'Not connected'}
                disabled
                className="field-input disabled"
              />
              <span className="field-note">Automatically detected from connection</span>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h3>Location</h3>
          <div className="detail-grid">
            <div className="detail-field">
              <label>Latitude</label>
              <input
                type="number"
                name="latitude"
                value={formData.latitude}
                onChange={handleChange}
                step="0.0001"
                className="field-input"
              />
              <span className="field-note">Pushed to the device on save (used for CPR decoding + range stats).</span>
            </div>
            <div className="detail-field">
              <label>Longitude</label>
              <input
                type="number"
                name="longitude"
                value={formData.longitude}
                onChange={handleChange}
                step="0.0001"
                className="field-input"
              />
              <span className="field-note">Pushed to the device on save (used for CPR decoding + range stats).</span>
            </div>
            <div className="detail-field">
              <label>Nearest Airport (ICAO)</label>
              <input
                type="text"
                name="airportIcao"
                value={formData.airportIcao}
                onChange={handleChange}
                maxLength="4"
                placeholder="e.g., KJFK"
                className="field-input"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowLocationPicker(true)}
            className="btn-location-full"
          >
            📍 Pick Location on Map
          </button>

          {showLocationPicker && (
            <div className="location-picker-container">
              <LocationPicker
                initialLat={formData.latitude ? parseFloat(formData.latitude) : null}
                initialLon={formData.longitude ? parseFloat(formData.longitude) : null}
                onLocationChange={handleLocationChange}
                onClose={() => setShowLocationPicker(false)}
              />
            </div>
          )}
        </div>

        <div className="detail-section">
          <h3>Hardware</h3>
          <div className="detail-grid">
            <div className="detail-field">
              <label>Receiver Hardware</label>
              <select
                name="receiverType"
                value={formData.receiverType}
                onChange={handleChange}
                className="field-input"
              >
                <option value="">Select hardware…</option>
                <option value="RTL-SDR">RTL-SDR</option>
                <option value="Airspy R2">Airspy R2</option>
                <option value="Airspy Mini">Airspy Mini</option>
                <option value="GNS Hulc">GNS Hulc</option>
                <option value="Mode-S Beast">Mode-S Beast</option>
                <option value="ADSBee 1090">ADSBee 1090</option>
                <option value="Beast-In (TCP)">Beast-In (TCP from external decoder)</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="detail-field">
              <label>Antenna Location</label>
              <select
                name="antennaType"
                value={formData.antennaType}
                onChange={handleChange}
                className="field-input"
              >
                <option value="">Select location...</option>
                <option value="indoor">Indoor</option>
                <option value="attic">Attic</option>
                <option value="outdoor-roof">Outdoor (Roof)</option>
                <option value="outdoor-balcony">Outdoor (Balcony)</option>
                <option value="outdoor-other">Outdoor (Other)</option>
              </select>
            </div>
            <div className="detail-field">
              <label>Antenna Height Above Ground (meters)</label>
              <input
                type="number"
                name="antennaHeightAboveGround"
                value={formData.antennaHeightAboveGround}
                onChange={handleChange}
                step="0.1"
                className="field-input"
              />
            </div>
            <div className="detail-field">
              <label>Receiver Software</label>
              <select
                name="softwareVersion"
                value={formData.softwareVersion}
                onChange={handleChange}
                className="field-input"
              >
                <option value="">Select software...</option>
                <option value="aviotrack-windows">Aviotrack Windows Receiver</option>
                <option value="aviotrack-mac">Aviotrack macOS Receiver</option>
                <option value="aviotrack-linux">Aviotrack Linux Receiver</option>
                <option value="readsb">readsb</option>
                <option value="dump1090">dump1090</option>
                <option value="dump1090-fa">dump1090-fa</option>
              </select>
            </div>
          </div>

          {formData.softwareVersion && (
            <SetupInstructions
              software={formData.softwareVersion}
              uuid={receiver.receiverUuid}
              onCopy={handleCopyInstruction}
              onNavigate={onNavigate}
            />
          )}
          {false && formData.softwareVersion && (
            <div className="config-instructions">
              <label>Setup Instructions - {formData.softwareVersion === 'aviotrack-windows' ? 'Aviotrack Windows Receiver' : formData.softwareVersion === 'aviotrack-linux' ? 'Aviotrack Linux Receiver' : formData.softwareVersion}</label>
              <div className="instructions-content">
                {formData.softwareVersion === 'aviotrack-windows' ? (
                  <ol>
                    <li>
                      <div className="instruction-item">
                        <span className="instruction-text">Download and run <strong>AviotrackReceiver.exe</strong> on your Windows PC.</span>
                      </div>
                    </li>
                    <li>
                      <div className="instruction-item">
                        <span className="instruction-text">Open the application and go to <strong>Settings</strong>.</span>
                      </div>
                    </li>
                    <li>
                      <div className="instruction-item">
                        <span className="instruction-text">In the server configuration, add your UUID to the server line:</span>
                        <div className="command-block">
                          <span className="command">feeders.aviotrack.com:{receiver.receiverUuid}</span>
                          <button
                            className="btn-copy"
                            onClick={() => handleCopyInstruction(`feeders.aviotrack.com:${receiver.receiverUuid}`)}
                            title="Copy server line"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4 2a2 2 0 0 1 2-2h4.586a1 1 0 0 1 .707.293l2.414 2.414a1 1 0 0 1 .293.707V11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2z" stroke="currentColor" strokeWidth="1" fill="none"/>
                              <path d="M2 6h2v7a1 1 0 0 0 1 1h5v2H5a2 2 0 0 1-2-2V6z" fill="currentColor"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="instruction-item">
                        <span className="instruction-text">Save the settings and start feeding!</span>
                      </div>
                    </li>
                    <li>
                      <div className="instruction-item">
                        <span className="instruction-text">You should immediately start seeing positions from your receiver on Aviotrack!</span>
                      </div>
                    </li>
                  </ol>
                ) : formData.softwareVersion === 'aviotrack-linux' ? (
                  <ol>
                    <li>
                      <div className="instruction-item">
                        <span className="instruction-text">Open a terminal on your Linux machine and run the install script:</span>
                        <div className="command-block">
                          <span className="command">curl -sL https://aviotrack-frontend.s3.eu-west-1.amazonaws.com/downloads/install.sh | sudo bash</span>
                          <button
                            className="btn-copy"
                            onClick={() => handleCopyInstruction('curl -sL https://aviotrack-frontend.s3.eu-west-1.amazonaws.com/downloads/install.sh | sudo bash')}
                            title="Copy command"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4 2a2 2 0 0 1 2-2h4.586a1 1 0 0 1 .707.293l2.414 2.414a1 1 0 0 1 .293.707V11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2z" stroke="currentColor" strokeWidth="1" fill="none"/>
                              <path d="M2 6h2v7a1 1 0 0 0 1 1h5v2H5a2 2 0 0 1-2-2V6z" fill="currentColor"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="instruction-item">
                        <span className="instruction-text">When prompted, enter your receiver UUID:</span>
                        <div className="command-block">
                          <span className="command">{receiver.receiverUuid}</span>
                          <button
                            className="btn-copy"
                            onClick={() => handleCopyInstruction(receiver.receiverUuid)}
                            title="Copy UUID"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4 2a2 2 0 0 1 2-2h4.586a1 1 0 0 1 .707.293l2.414 2.414a1 1 0 0 1 .293.707V11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2z" stroke="currentColor" strokeWidth="1" fill="none"/>
                              <path d="M2 6h2v7a1 1 0 0 0 1 1h5v2H5a2 2 0 0 1-2-2V6z" fill="currentColor"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="instruction-item">
                        <span className="instruction-text">The installer will set up readsb and configure it to feed to Aviotrack automatically.</span>
                      </div>
                    </li>
                    <li>
                      <div className="instruction-item">
                        <span className="instruction-text">You should immediately start seeing positions from your receiver on Aviotrack!</span>
                      </div>
                    </li>
                  </ol>
                ) : formData.softwareVersion === 'readsb' ? (
                  <ol>
                    <li>
                      <div className="instruction-item">
                        <span className="instruction-text">Open a terminal connection to your raspberry pi.</span>
                      </div>
                    </li>
                    <li>
                      <div className="instruction-item">
                        <span className="instruction-text">Open the readsb configuration file in an editor e.g.</span>
                        <div className="command-block">
                          <span className="command">sudo nano /etc/default/readsb</span>
                          <button
                            className="btn-copy"
                            onClick={() => handleCopyInstruction('sudo nano /etc/default/readsb')}
                            title="Copy command"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4 2a2 2 0 0 1 2-2h4.586a1 1 0 0 1 .707.293l2.414 2.414a1 1 0 0 1 .293.707V11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2z" stroke="currentColor" strokeWidth="1" fill="none"/>
                              <path d="M2 6h2v7a1 1 0 0 0 1 1h5v2H5a2 2 0 0 1-2-2V6z" fill="currentColor"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="instruction-item">
                        <span className="instruction-text">Add the following block to the RECEIVER_OPTIONS line</span>
                        <div className="command-block">
                          <span className="command">--net-connector feeders.aviotrack.com,30009,beast_reduce_plus_out,uuid={receiver.receiverUuid}</span>
                          <button
                            className="btn-copy"
                            onClick={() => handleCopyInstruction(`--net-connector feeders.aviotrack.com,30009,beast_reduce_plus_out,uuid=${receiver.receiverUuid}`)}
                            title="Copy command"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4 2a2 2 0 0 1 2-2h4.586a1 1 0 0 1 .707.293l2.414 2.414a1 1 0 0 1 .293.707V11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2z" stroke="currentColor" strokeWidth="1" fill="none"/>
                              <path d="M2 6h2v7a1 1 0 0 0 1 1h5v2H5a2 2 0 0 1-2-2V6z" fill="currentColor"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="instruction-item">
                        <span className="instruction-text">Save (Ctrl-X Yes to save). Restart readsb e.g.</span>
                        <div className="command-block">
                          <span className="command">sudo systemctl restart readsb</span>
                          <button
                            className="btn-copy"
                            onClick={() => handleCopyInstruction('sudo systemctl restart readsb')}
                            title="Copy command"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4 2a2 2 0 0 1 2-2h4.586a1 1 0 0 1 .707.293l2.414 2.414a1 1 0 0 1 .293.707V11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2z" stroke="currentColor" strokeWidth="1" fill="none"/>
                              <path d="M2 6h2v7a1 1 0 0 0 1 1h5v2H5a2 2 0 0 1-2-2V6z" fill="currentColor"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="instruction-item">
                        <span className="instruction-text">You should immediately start seeing positions from your receiver on Aviotrack!</span>
                      </div>
                    </li>
                  </ol>
                ) : (
                  <div className="coming-soon-message">
                    <p>Coming Soon!</p>
                    <p className="coming-soon-hint">Configuration instructions for {formData.softwareVersion} are being prepared. In the meantime, you can use a similar approach to readsb.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="detail-section">
          <h3>Configuration</h3>
          <div className="detail-grid">
            <div className="detail-field">
              <label>Timezone</label>
              <select
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                className="field-input"
              >
                {timezones.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
            <div className="detail-field">
              <label>Distance Units</label>
              <select
                name="distanceUnits"
                value={formData.distanceUnits}
                onChange={handleChange}
                className="field-input"
              >
                <option value="metric">Metric (km)</option>
                <option value="aeronautical">Aeronautical (NM)</option>
              </select>
            </div>
            <div className="detail-field">
              <label>Range Rings</label>
              <input
                type="number"
                name="rangeRingsNumber"
                value={formData.rangeRingsNumber}
                onChange={handleChange}
                min="0"
                max="20"
                className="field-input"
              />
            </div>
            <div className="detail-field">
              <label>Ring Interval</label>
              <input
                type="number"
                name="rangeRingsInterval"
                value={formData.rangeRingsInterval}
                onChange={handleChange}
                min="1"
                className="field-input"
              />
            </div>
            <div className="detail-field">
              <label>Max Distance</label>
              <input
                type="number"
                name="maxDistance"
                value={formData.maxDistance}
                onChange={handleChange}
                min="1"
                className="field-input"
              />
            </div>
            <div className="detail-field">
              <label>UpInTheAir Code</label>
              <input
                type="text"
                name="upInTheAirCode"
                value={formData.upInTheAirCode}
                onChange={handleChange}
                maxLength="8"
                placeholder="8-digit code"
                className="field-input"
              />
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h3>Receiver Mode</h3>
          <div className="checkbox-group">
            <div className="detail-field checkbox-field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isMobile"
                  checked={formData.isMobile}
                  onChange={handleChange}
                />
                <span>Mobile receiver</span>
              </label>
            </div>
          </div>
          {formData.isMobile && (
            <span className="field-note">Mobile receivers disable range validation and allow GPS location updates over the socket connection. Location and range rings will not be shown on the map.</span>
          )}
        </div>

        <div className="detail-section">
          <h3>Arrival/Departure Detection</h3>
          <div className="checkbox-group">
            <div className="detail-field checkbox-field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="airborneDetectionEnabled"
                  checked={formData.airborneDetectionEnabled}
                  onChange={handleChange}
                />
                <span>Enable airborne detection (detect arrivals/departures from flight phase)</span>
              </label>
            </div>
            <div className="detail-field checkbox-field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="simulationEnabled"
                  checked={formData.simulationEnabled}
                  onChange={handleChange}
                />
                <span>Enable time simulation (estimate missing timestamps)</span>
              </label>
            </div>
          </div>
          <div className="detail-grid">
            <div className="detail-field full-width">
              <label>Detection Airports (ICAO codes)</label>
              <input
                type="text"
                name="detectionAirportList"
                value={formData.detectionAirportList}
                onChange={handleChange}
                placeholder="e.g., LPPR,LPPT,LPFR (empty = all airports)"
                className="field-input"
              />
              <span className="field-note">Comma-separated ICAO codes. Leave empty to detect all airports within range.</span>
            </div>
          </div>
          <div className="detail-grid">
            <div className="detail-field">
              <label>Max Receiver-Airport Range (km)</label>
              <input
                type="number"
                name="maxReceiverAirportRangeKm"
                value={formData.maxReceiverAirportRangeKm}
                onChange={handleChange}
                min="1"
                max="200"
                className="field-input"
              />
            </div>
            <div className="detail-field">
              <label>Max Aircraft-Airport Range (km)</label>
              <input
                type="number"
                name="maxAircraftAirportRangeKm"
                value={formData.maxAircraftAirportRangeKm}
                onChange={handleChange}
                min="1"
                max="100"
                className="field-input"
              />
            </div>
            <div className="detail-field">
              <label>Max Detection Altitude (ft)</label>
              <input
                type="number"
                name="maxDetectionAltitudeFt"
                value={formData.maxDetectionAltitudeFt}
                onChange={handleChange}
                min="1000"
                max="45000"
                step="500"
                className="field-input"
              />
              <span className="field-note">Aircraft above this altitude will not be detected as arriving/departing.</span>
            </div>
          </div>
          <div className="detail-grid">
            <div className="detail-field checkbox-field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="requireRunwayAlignment"
                  checked={formData.requireRunwayAlignment}
                  onChange={handleChange}
                />
                <span>Require runway alignment for detection</span>
              </label>
            </div>
            {formData.requireRunwayAlignment && (
              <div className="detail-field">
                <label>Runway Heading Tolerance (°)</label>
                <input
                  type="number"
                  name="runwayHeadingToleranceDeg"
                  value={formData.runwayHeadingToleranceDeg}
                  onChange={handleChange}
                  min="5"
                  max="90"
                  className="field-input"
                />
                <span className="field-note">Allowed deviation from runway centreline heading.</span>
              </div>
            )}
          </div>
        </div>

        <div className="detail-section">
          <h3>Notes</h3>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="4"
            placeholder="Additional information about this receiver..."
            className="field-textarea"
          />
        </div>

        {/* Viewer Management Section */}
        <ViewerManagement
          ref={viewerManagementRef}
          receiverUuid={receiver.receiverUuid}
          token={token}
          onPendingChange={setHasPendingViewer}
        />

        {onDelete && (
          <div className="detail-section">
            <h3 style={{ color: '#dc3545' }}>Danger Zone</h3>
            <p style={{ marginBottom: '12px', color: '#666' }}>
              Deleting this receiver will permanently remove all associated data including statistics and history.
              This action cannot be undone.
            </p>
            <button
              className="btn-delete"
              onClick={handleDeleteClick}
              type="button"
            >
              Delete Receiver
            </button>
          </div>
        )}
      </div>

      {/* Sticky Save Button Footer */}
      <div className="save-footer">
        <button
          className="btn-save-detail"
          onClick={handleSave}
          disabled={!hasChanges && !hasPendingViewer}
        >
          {hasChanges || hasPendingViewer ? 'Save Changes' : 'No Changes'}
        </button>
      </div>
      </>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ color: '#dc3545' }}>⚠️ Confirm Deletion</h2>
              <button className="close-button" onClick={handleDeleteCancel}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', fontSize: '16px' }}>
                Are you sure you want to delete receiver <strong>{receiver.receiverName}</strong>?
              </p>
              <p style={{ marginBottom: '16px', color: '#721c24' }}>
                This will permanently delete:
              </p>
              <ul style={{ marginBottom: '16px', paddingLeft: '24px', color: '#721c24' }}>
                <li>All receiver configuration</li>
                <li>All historical statistics</li>
                <li>All aircraft tracking data</li>
                <li>All furthest distance records</li>
              </ul>
              <p style={{ fontWeight: 'bold', color: '#dc3545' }}>
                This action cannot be undone!
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel-detail" onClick={handleDeleteCancel}>
                Cancel
              </button>
              <button className="btn-delete" onClick={handleDeleteConfirm} style={{ marginTop: 0 }}>
                Yes, Delete Receiver
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReceiverDetail;
